import { getUuidV4 } from '../../utilities/generic-utilities';
import { AddressState } from './address-collection.state';

/**
 * The prefix marking an address number the client minted itself. Every address in the slice carries
 * an `addrNumber`, but only the backend can hand out a real one: an address the user creates by
 * hand exists locally long before — if ever — the backend learns about it, and until then it is
 * addressed by a number of this form.
 *
 * The prefix is what tells the two apart, so nothing may send a manual number to the backend (see
 * {@link hasBackendAddrNumber}) and every "is this address known to the backend?" question is asked
 * through the predicates below rather than by testing `addrNumber` for emptiness.
 */
export const MANUAL_ADDR_NUMBER_PREFIX = 'manual-';

/**
 * The manual address number for an address, derived from its slice identity so that the same
 * address keeps the same number across reloads.
 *
 * @param id the address's {@link AddressState.id}
 */
export function manualAddrNumber(id: string): string {
    return `${MANUAL_ADDR_NUMBER_PREFIX}${id}`;
}

/**
 * Whether the number was minted on this device, i.e. the backend does not know the address yet.
 *
 * @param addrNumber
 */
export function isManualAddrNumber(addrNumber: string | undefined): boolean {
    return addrNumber?.startsWith(MANUAL_ADDR_NUMBER_PREFIX) ?? false;
}

/**
 * Whether the address number is one the backend handed out — the question every caller that used to
 * test `addrNumber` for emptiness is really asking. A manual number is not one, and neither is a
 * missing one.
 *
 * @param addrNumber
 */
export function hasBackendAddrNumber(addrNumber: string | undefined): boolean {
    return addrNumber !== undefined && addrNumber.length > 0 && !isManualAddrNumber(addrNumber);
}

/**
 * The address number as the backend has to see it: a manual number is the client's own bookkeeping
 * and stands for "no address number", so it is sent as the empty string the backend expects for an
 * address it does not know.
 *
 * @param addrNumber
 */
export function outboundAddrNumber(addrNumber: string | undefined): string {
    return addrNumber !== undefined && hasBackendAddrNumber(addrNumber) ? addrNumber : '';
}

/**
 * Whether the technician typed this address by hand, rather than it having come from the backend.
 * This is the one place that answers "did the technician type this address?" — the collection holds
 * exactly three kinds of address, and this predicate is what tells the first apart from the other
 * two:
 *
 * - a **user-created** address (this predicate) — typed by a technician;
 * - a **customer address** — the flat `Name1`/`Street`/… header fields of an OData notification,
 *   built by `customerAddressToState`;
 * - an **AddressSet address** — a notification's own short, hideable rows, built by
 *   `shortAddressToState`.
 *
 * The two backend kinds both carry `isFromAddressSet: false` and `true` respectively, but a
 * customer address is not from the address set and still carries a real backend `addrNumber` — so
 * `isFromAddressSet` alone cannot single out the one kind the backend never supplied.
 *
 * @param address
 */
export function isUserCreatedAddress(address: AddressState): boolean {
    return !address.isFromAddressSet && !hasBackendAddrNumber(address.addrNumber);
}

/**
 * The collection with the slice's invariant restored: every address has an id and an `addrNumber`,
 * a manual one where the backend has not supplied its own. Applied wherever a collection enters the
 * slice — storage hydration included, since addresses persisted before manual numbers existed carry
 * neither.
 *
 * Only the legacy path without an id has to mint anything; an address that has one derives its
 * manual number from it, so re-normalizing a collection leaves it untouched — the collection itself
 * included, which is returned as it came when it already satisfies the invariant.
 *
 * @param addresses the collection about to enter the slice
 */
export function withAddressNumbers(addresses: AddressState[]): AddressState[] {
    if (addresses.every((address) => address.id && address.addrNumber)) {
        return addresses;
    }
    return addresses.map((address) => {
        if (address.id && address.addrNumber) {
            return address;
        }
        const id = address.id || getUuidV4();
        return { ...address, id, addrNumber: address.addrNumber || manualAddrNumber(id) };
    });
}
